import { type JSX, useState } from 'react';

import { footerColumns } from '../navigation/site-navigation';
import { regionForLocale, useI18n } from '../i18n';
import { useContentOverride } from '../content/ContentProvider';
import { routes } from '../routes';
import { DestinationLink, PageContainer } from './primitives';
import { LanguageModal } from './LanguageModal';

/**
 * The footer.
 *
 * On a marketplace this is the site map, and for many people it is the primary
 * navigation — the place you go when the page you want was not on the page you
 * landed on. So it carries the whole platform: six columns rendered from the
 * same `site-navigation` declaration the mega menu uses, which is what stops
 * the two drifting apart.
 *
 * Destinations that are not built yet still appear. Leaving them out would
 * quietly design the platform down to what exists this week; they render as
 * disabled controls that say so.
 *
 * The dark background stays full-bleed while the content sits in the shared
 * `PageContainer`, so the first column starts on the same line as the header's
 * wordmark and the headings above it.
 *
 * No legal copy is invented. The legal links point at routes that will hold
 * pages written by people qualified to write them.
 */

/**
 * Social accounts.
 *
 * Empty until the accounts exist. A footer icon linking to a profile nobody
 * has registered is worse than no icon: it is a dead end wearing a brand, and
 * it is how someone else's account ends up looking official.
 */
const socialLinks: readonly { readonly label: string; readonly href: string }[] = [];

export function JTFooter(): JSX.Element {
  const { locale, t } = useI18n();
  const edited = useContentOverride();
  const [languageOpen, setLanguageOpen] = useState(false);
  const year = new Date().getFullYear();

  const region = regionForLocale(locale);

  return (
    <footer className="jt-footer">
      <PageContainer className="jt-footer__inner">
        <div className="jt-footer__top">
          <div className="jt-footer__brand">
            <span className="jt-footer__word">Just Treated</span>
            <p className="jt-footer__blurb">{edited('footer.blurb') ?? t('footer.blurb')}</p>
          </div>

          <nav className="jt-footer__nav" aria-label={t('footer.navLabel')}>
            {footerColumns.map((column) => (
              <div key={column.key} className="jt-footer__group">
                <h2 className="jt-footer__heading">
                  {edited(column.contentKey as never) ?? t(column.titleKey)}
                </h2>
                <ul className="jt-footer__links">
                  {column.links.map((link) => (
                    <li key={link.path}>
                      <DestinationLink destination={link} className="jt-footer__link">
                        {link.label}
                      </DestinationLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </div>

        <div className="jt-footer__bar">
          <span className="jt-footer__regulator">
            Regulated by
            <a
              href="https://www.cqc.org.uk/location/1-26088938261"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="View our Care Quality Commission rating (opens in new tab)"
              className="jt-footer__cqc"
            >
              <img
                src="/home/footer/cqc-logo.webp"
                alt="Care Quality Commission"
                width="72"
                height="54"
                style={{ objectFit: 'cover', display: 'block', borderRadius: 6 }}
                loading="lazy"
              />
            </a>
            {/*
              The translated line unless an editor has genuinely replaced it. It
              differs by locale — the GPhC regulates pharmacy in Great Britain, so
              the global wording must not claim registration with it — and an
              unedited block must not flatten that back to the British sentence.
            */}
            <span>{edited('footer.legal-line') ?? t('footer.legalLine', { year })}</span>
          </span>

          <span className="jt-footer__legal">
            <button
              type="button"
              className="jt-footer__region"
              onClick={() => setLanguageOpen(true)}
              aria-haspopup="dialog"
            >
              <span aria-hidden="true">{region.flag}</span>
              {t('footer.regionLabel')}
            </button>

            <DestinationLink destination={routes.privacy} className="jt-footer__link">
              {routes.privacy.label}
            </DestinationLink>
            <DestinationLink destination={routes.terms} className="jt-footer__link">
              {routes.terms.label}
            </DestinationLink>
            <DestinationLink destination={routes.cookies} className="jt-footer__link">
              {routes.cookies.label}
            </DestinationLink>
            <DestinationLink destination={routes.accessibility} className="jt-footer__link">
              {routes.accessibility.label}
            </DestinationLink>

            {socialLinks.length > 0 ? (
              <span className="jt-footer__social" aria-label={t('footer.social')}>
                {socialLinks.map((social) => (
                  <a
                    key={social.href}
                    href={social.href}
                    className="jt-footer__link"
                    rel="noreferrer noopener"
                    target="_blank"
                  >
                    {social.label}
                  </a>
                ))}
              </span>
            ) : null}
          </span>
        </div>
      </PageContainer>

      {/* The footer's own instance: reachable without scrolling back up. */}
      <LanguageModal open={languageOpen} onClose={() => setLanguageOpen(false)} />
    </footer>
  );
}
